import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useTheme from 'api/useTheme';
import { EFeatureFlag } from 'constants/enums';
import { useFormikContext } from 'formik';
import { Base64 } from 'js-base64';
import { extractColorValue } from 'utils/colorUtils';
import { ThemeLoader } from '../ThemeLoader';

import {
  defaultCheckoutThemeV2,
  defaultCheckoutThemeV3,
  getDefaultBackgroundImageByVersion,
  getDefaultFontColorByVersion
} from './defaults';
import CheckoutThemePreviewV2 from './Previews/CheckoutThemePreviewV2';
import CheckoutThemePreviewV3 from './Previews/CheckoutThemePreviewV3';
import CheckoutThemeV3 from './Themes/CheckoutThemeV3';
import CheckoutThemeV2 from './Themes/CheckoutThemeV2';
import { CheckoutThemeFormValues } from './types';
import 'style/forms.scss';

const CheckoutTheme = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<CheckoutThemeFormValues>();
  const { publisherId } = useParams();
  const { featureFlags } = useFeatureFlags({ publisherId });

  const isCheckoutV3Enabled = useMemo(
    () => featureFlags?.[EFeatureFlag.CHECKOUT_V3],
    [featureFlags]
  );

  const getDefaults = () => {
    const defaultCheckoutTheme = isCheckoutV3Enabled
      ? defaultCheckoutThemeV3
      : defaultCheckoutThemeV2;
    return {
      footerConfiguration: defaultCheckoutTheme.footerConfiguration,
      logo: defaultCheckoutTheme.logo,
      primaryColor: defaultCheckoutTheme.primaryColor,
      textColor: defaultCheckoutTheme.textColor,
      backgroundImage: defaultCheckoutTheme.backgroundImage,
      mobileBgImage: defaultCheckoutTheme.mobileBgImage,
      buttonColor: defaultCheckoutTheme.buttonColor,
      buttonTextColor: defaultCheckoutTheme.buttonTextColor,
      publisherId: publisherId || '',
      _id: ''
    };
  };

  const defaultValues = useMemo(() => {
    const defaults = getDefaults();

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      logo: values.logo || defaults.logo,
      primaryColor:
        extractColorValue(values.primaryColor) || defaults.primaryColor,
      textColor: getDefaultFontColorByVersion(
        isCheckoutV3Enabled,
        values.textColor
      ),
      footerConfiguration: {
        DEFAULT: {
          richText: Base64.encode('')
        },
        ...(values.footerConfiguration ?? {}),
        ...(defaults.footerConfiguration ?? {})
      },
      backgroundImage: getDefaultBackgroundImageByVersion(
        isCheckoutV3Enabled,
        values.backgroundImage
      ),
      mobileBgImage: values.mobileBgImage || defaults.mobileBgImage,
      buttonColor:
        extractColorValue(values.buttonColor) || defaults.buttonColor,
      buttonTextColor:
        extractColorValue(values.buttonTextColor) || defaults.buttonTextColor,
      publisherId: values.publisherId || defaults.publisherId,
      _id: values._id || defaults._id
    };
  }, [values, publisherId, isCheckoutV3Enabled]);

  const { getCheckoutTheme: checkoutTheme } = useTheme(publisherId);

  const pictures = useImages(publisherId).getImages;
  const [showPreview, setShowPreview] = useState(false);

  const hasValues = useMemo(
    () => values && Object.keys(values).length > 0,
    [values]
  );

  useEffect(
    () =>
      setIsLoading(
        checkoutTheme.isLoading || !checkoutTheme.data || !hasValues
      ),
    [checkoutTheme?.isLoading, checkoutTheme?.data, hasValues]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <>
      {showPreview ? (
        isCheckoutV3Enabled ? (
          <CheckoutThemePreviewV3
            fontFamily={'Inter'}
            logo={defaultValues.logo}
            bgImage={defaultValues.backgroundImage}
            bgMobileImage={defaultValues.mobileBgImage}
            textColor={defaultValues.textColor}
            onClose={() => setShowPreview(false)}
          />
        ) : (
          <CheckoutThemePreviewV2
            logo={defaultValues.logo}
            bgImage={defaultValues.backgroundImage}
            bgMobileImage={defaultValues.mobileBgImage || ''}
            primaryColor={defaultValues.primaryColor || ''}
            textColor={defaultValues.textColor}
            buttonColor={defaultValues.buttonColor || ''}
            buttonTextColor={defaultValues.buttonTextColor || ''}
            onClose={() => setShowPreview(false)}
          />
        )
      ) : pictures.isLoading ? (
        <ThemeLoader />
      ) : isCheckoutV3Enabled ? (
        <CheckoutThemeV3
          defaultValues={defaultValues}
          pictures={pictures}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          setShowPreview={setShowPreview}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
          values={values}
        />
      ) : (
        <CheckoutThemeV2
          defaultValues={defaultValues}
          pictures={pictures}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          setShowPreview={setShowPreview}
          handleBlur={handleBlur}
          touched={touched}
          errors={errors}
          values={values}
        />
      )}
    </>
  );
};

export default CheckoutTheme;
